import { YonduOAuthInteraction } from '@ver.id/libs.ui.yondu';
import { runEmbeddedResumeLoop } from './embeddedResumeLoop';
import { type RonanParentMessage } from '../hooks/useParentMessaging';

/**
 * How the journey outcome reaches the client:
 * - `redirect`: navigate the top-level window to the OAuth resume endpoint.
 * - `webhook`: embedded mode — drive the flow with fetches and report the
 *   outcome to the parent frame via postMessage.
 */
export type DeliveryMode = 'redirect' | 'webhook';

/** Why the journey is being rejected — decides the message the parent receives. */
export type RejectIntent = 'cancel' | 'error';

/**
 * Whether the journey reached a terminal state.
 * - `settled`: the outcome was delivered (or a terminal error was reported);
 *   the caller keeps its redirect guard engaged.
 * - `aborted`: delivery failed before the journey ended; the caller releases
 *   the guard (and, for rejects, falls back to the generic error page).
 */
export type DeliveryOutcome = 'settled' | 'aborted';

/**
 * Everything a delivery strategy needs, expressed without React so the
 * strategies stay plain and directly testable.
 */
export type DeliveryContext = {
  /**
   * The live OAuth interaction. `set` is called when the embedded resume loop
   * advances to a follow-up interaction, keeping the caller in sync.
   */
  interaction: {
    get: () => YonduOAuthInteraction | null;
    set: (next: YonduOAuthInteraction) => void;
  };
  /** Post a message to the parent frame. No-op outside embedded mode. */
  notifyParent: (message: RonanParentMessage) => void;
};

export type JourneyDelivery = {
  /** Consent given — settle the interaction and hand the result to the client. */
  complete: (ctx: DeliveryContext) => Promise<DeliveryOutcome>;
  /** End the OAuth journey without consent. Never touches Chulak. */
  reject: (
    ctx: DeliveryContext,
    intent: RejectIntent,
  ) => Promise<DeliveryOutcome>;
};

/** The message a webhook-mode reject reports for a given intent. */
function rejectMessage(intent: RejectIntent): RonanParentMessage {
  return intent === 'cancel'
    ? { type: 'ronan:cancel' }
    : { type: 'ronan:error', error: 'access_denied' };
}

/**
 * Redirect delivery — the standalone (non-embedded) journey. Both outcomes end
 * by navigating to the resume endpoint, which redirects on to the client.
 *
 * Failures are not recoverable here: `complete` rethrows so the caller can
 * surface it, and `reject` reports `aborted` so the caller can fall back to a
 * generic error page (there is no way back to the client at that point).
 */
export const redirectDelivery: JourneyDelivery = {
  async complete(ctx) {
    const interaction = ctx.interaction.get();
    if (!interaction) throw new Error('No OAuth interaction available');

    await interaction.finalize({ consentGiven: true });
    window.location.replace(interaction.getResumeUri());
    return 'settled';
  },

  async reject(ctx) {
    const interaction = ctx.interaction.get();
    // No live OAuth interaction to redirect through.
    if (!interaction) return 'aborted';

    try {
      await interaction.finalize({ error: 'AgentCancel' });
      window.location.replace(interaction.getResumeUri());
      return 'settled';
    } catch {
      // On context_expired or network failure there's no way back.
      return 'aborted';
    }
  },
};

/**
 * Webhook delivery — the embedded journey. Nothing navigates: the flow is
 * advanced with fetches and every outcome, including failures, is reported to
 * the parent frame. Consequently these methods never throw.
 */
export const webhookDelivery: JourneyDelivery = {
  async complete(ctx) {
    const interaction = ctx.interaction.get();
    if (!interaction) throw new Error('No OAuth interaction available');

    try {
      await interaction.finalize({ consentGiven: true });

      const result = await runEmbeddedResumeLoop(
        interaction,
        ctx.interaction.set,
      );

      if (result.status === 'completed') {
        ctx.notifyParent({ type: 'ronan:complete' });
      } else {
        ctx.notifyParent({
          type: 'ronan:error',
          error:
            result.status === 'error'
              ? result.error
              : 'max_iterations_exceeded',
        });
      }
      return 'settled';
    } catch (e) {
      ctx.notifyParent({
        type: 'ronan:error',
        error: e instanceof Error ? e.message : 'complete_failed',
      });
      return 'aborted';
    }
  },

  async reject(ctx, intent) {
    const interaction = ctx.interaction.get();
    if (!interaction) {
      ctx.notifyParent(rejectMessage(intent));
      return 'settled';
    }

    try {
      await interaction.finalize({ error: 'AgentCancel' });
      ctx.notifyParent(rejectMessage(intent));
    } catch {
      // Finalize failed — the journey is over either way, report it as an error.
      ctx.notifyParent({ type: 'ronan:error', error: 'access_denied' });
    }
    return 'settled';
  },
};

export function getDelivery(mode: DeliveryMode): JourneyDelivery {
  return mode === 'webhook' ? webhookDelivery : redirectDelivery;
}
